/**
 * Frontend fallback utilities for extracting structured decision sheet data
 * from markdown verdict text, when the backend doesn't provide structured fields.
 */

/**
 * Extract TL;DR content from a markdown verdict.
 * Looks for ## TL;DR or ## Bottom Line section, returns the text.
 * Falls back to the first 2-3 sentences of the verdict.
 * Preserves markdown formatting (bold, bullets, links) for proper rendering.
 */
export function extractTldr(verdict: string): string {
  if (!verdict) return '';

  // Try to find a TL;DR section — preserve markdown structure
  const tldrMatch = verdict.match(
    /##\s*(?:TL;DR|Bottom Line|Summary|Key Takeaway)[:\s]*\n([\s\S]*?)(?=\n##|\n---|\z)/i,
  );
  if (tldrMatch) {
    return tldrMatch[1].trim().slice(0, 800);
  }

  // Fallback: first 2-3 sentences (preserve markdown inline formatting)
  const cleaned = verdict
    .replace(/^#+\s+.*$/gm, '') // strip headers only
    .trim();
  const sentences = cleaned.match(/[^.!?]*[.!?]/g);

  if (sentences && sentences.length > 0) {
    return sentences.slice(0, 3).join(' ').trim();
  }

  // Last resort: first 400 chars
  return verdict.slice(0, 400).trim();
}

/**
 * Extract action items from a markdown verdict.
 * Looks for ## Recommended Actions / ## What To Do / ## Next Steps section.
 */
export function extractActionItems(verdict: string): string[] {
  if (!verdict) return [];

  const actionsMatch = verdict.match(
    /##\s*(?:Recommended Actions|What To Do|Next Steps|Action Items|Actionable Conclusions)[:\s]*\n([\s\S]*?)(?=\n##|\n---|\z)/i,
  );
  if (!actionsMatch) return [];

  const section = actionsMatch[1];

  // Extract numbered or bulleted items
  const items: string[] = [];
  const lines = section.split('\n');
  for (const line of lines) {
    const match = line.match(/^\s*(?:\d+[.)]\s*|[-*•]\s*)(.*)/);
    if (match && match[1].trim()) {
      items.push(match[1].trim());
    }
  }

  return items.slice(0, 5); // Cap at 5 items
}

/**
 * Extract corrections from debate states.
 * Returns items where the debate resulted in a factual correction.
 */
export function extractCorrections(
  debatesPerTask: Map<number, { corrections?: Array<{ original: string; corrected: string; source?: string }> }>,
): Array<{ original: string; corrected: string; source?: string }> {
  const corrections: Array<{ original: string; corrected: string; source?: string }> = [];

  for (const [, debate] of debatesPerTask) {
    if (debate.corrections) {
      corrections.push(...debate.corrections);
    }
  }

  return corrections;
}

/**
 * Derive a TL;DR for financial analysis from kill sheet data.
 */
export function deriveFinancialTldr(killSheet: {
  winning_side: 'bull' | 'bear';
  confidence: number;
  kill_sheet_reason?: string;
  decisive_factor?: string;
  ticker: string;
}): string {
  const side = killSheet.winning_side === 'bull' ? 'Bull' : 'Bear';
  const confidence = Math.round(killSheet.confidence);
  const reason = killSheet.kill_sheet_reason || killSheet.decisive_factor || '';

  return `${side} case wins at ${confidence}% confidence. ${reason}`.trim();
}

/**
 * Derive action items for hold/entry from financial kill sheet data.
 */
export function deriveFinancialActions(killSheet: {
  winning_side: 'bull' | 'bear';
  catalysts?: string[];
  risks?: string[];
  watch_items?: Array<{ trigger: string; impact: string; timeframe: string }>;
}): { holdActions: string[]; entryActions: string[] } {
  const holdActions: string[] = [];
  const entryActions: string[] = [];

  if (killSheet.winning_side === 'bear') {
    // Bear case: suggest caution for holders, patience for potential buyers
    if (killSheet.risks?.length) {
      holdActions.push(`Key risk: ${killSheet.risks[0]}`);
    }
    if (killSheet.watch_items?.length) {
      holdActions.push(`Watch: ${killSheet.watch_items[0].trigger} (${killSheet.watch_items[0].timeframe})`);
    }
    if (killSheet.catalysts?.length) {
      entryActions.push(`Wait for: ${killSheet.catalysts[0]}`);
    }
  } else {
    // Bull case: suggest holding for holders, consider entry for buyers
    if (killSheet.catalysts?.length) {
      holdActions.push(`Catalyst: ${killSheet.catalysts[0]}`);
    }
    if (killSheet.risks?.length) {
      entryActions.push(`Risk to monitor: ${killSheet.risks[0]}`);
    }
    if (killSheet.watch_items?.length) {
      entryActions.push(`Watch: ${killSheet.watch_items[0].trigger} (${killSheet.watch_items[0].timeframe})`);
    }
  }

  return { holdActions, entryActions };
}

/**
 * Extract TL;DR from strategic deliverable.
 * Uses first 2-3 sentences of executive summary or kill sheet recommendation.
 */
export function extractStrategicTldr(deliverable: {
  executiveSummary?: string;
  killSheet?: { recommendation?: string };
}): string {
  const source = deliverable.killSheet?.recommendation || deliverable.executiveSummary || '';
  if (!source) return '';

  // Take first 2-3 sentences
  const sentences = source
    .replace(/^#+\s+.*$/gm, '')
    .replace(/\n+/g, ' ')
    .trim()
    .match(/[^.!?]*[.!?]/g);

  if (sentences && sentences.length > 0) {
    return sentences.slice(0, 3).join(' ').trim();
  }

  return source.slice(0, 400).trim();
}
